import React, { useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { FaChevronDown, FaCheck } from 'react-icons/fa';

const SIZES = {
  sm: { height: 32, padX: 12, fontSize: 12, icon: 10 },
  md: { height: 38, padX: 14, fontSize: 13, icon: 12 },
  lg: { height: 44, padX: 16, fontSize: 14, icon: 14 },
};

const TwDropdown = ({
  value, onChange, options = [], leftIcon: LeftIcon,
  size = 'sm', placeholder = 'Select...',
  minWidth, menuWidth, align = 'start',
  disabled = false, className = '',
}) => {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const sz = SIZES[size];
  const current = options.find((o) => o.value === value);

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const width = parseInt(menuWidth, 10) || r.width;
    let left = align === 'end' ? r.right - width : r.left;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
    if (left < 8) left = 8;
    setPos({ top: r.bottom + 4, left, width });
  }, [menuWidth, align]);

  useLayoutEffect(() => { if (open) updatePos(); }, [open, updatePos]);

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => updatePos();
    const onResize = () => updatePos();
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)
          && triggerRef.current && !triggerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updatePos]);

  const handleSelect = (opt) => {
    onChange?.(opt.value, opt);
    setOpen(false);
  };

  const triggerStyle = {
    appearance: 'none',
    WebkitAppearance: 'none',
    boxShadow: 'none',
    textTransform: 'none',
    letterSpacing: 'normal',
    fontFamily: 'inherit',
    margin: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    height: `${sz.height}px`,
    paddingLeft: `${sz.padX}px`,
    paddingRight: `${sz.padX - 2}px`,
    paddingTop: 0,
    paddingBottom: 0,
    fontSize: `${sz.fontSize}px`,
    fontWeight: 700,
    lineHeight: 1.4,
    color: open ? '#0f172a' : (hover ? '#0f172a' : '#475569'),
    backgroundColor: hover ? '#f8fafc' : '#ffffff',
    border: `1px solid ${open ? '#0f172a' : (hover ? '#cbd5e1' : '#e2e8f0')}`,
    borderRadius: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    minWidth: minWidth || 'auto',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    flexShrink: 0,
    transition: 'background-color 120ms ease, border-color 120ms ease',
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((p) => !p)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={className}
        style={triggerStyle}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flex: 1 }}>
          {LeftIcon && <LeftIcon style={{ color: '#94a3b8', flexShrink: 0 }} size={sz.icon} />}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
            {current ? current.label : <span style={{ color: '#94a3b8', fontWeight: 500 }}>{placeholder}</span>}
          </span>
        </span>
        <FaChevronDown
          style={{ color: '#94a3b8', flexShrink: 0, transition: 'transform 120ms', transform: open ? 'rotate(180deg)' : 'none' }}
          size={sz.icon - 1}
        />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: pos.width,
            maxHeight: '280px',
            zIndex: 100001,
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            boxShadow: '0 10px 30px rgba(15,23,42,0.12), 0 2px 6px rgba(15,23,42,0.06)',
            overflow: 'auto',
            padding: '4px 0',
          }}
        >
          {options.map((opt) => {
            const selected = opt.value === value;
            const OptIcon = opt.icon;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleSelect(opt)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = selected ? '#f1f5f9' : '#f8fafc'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = selected ? '#f8fafc' : 'transparent'; }}
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  boxShadow: 'none',
                  margin: 0,
                  width: '100%',
                  border: 'none',
                  backgroundColor: selected ? '#f8fafc' : 'transparent',
                  color: selected ? '#0f172a' : '#475569',
                  fontWeight: selected ? 700 : 500,
                  fontSize: 12,
                  fontFamily: 'inherit',
                  lineHeight: 1.4,
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'background-color 100ms ease',
                }}
              >
                {OptIcon && <OptIcon size={10} style={{ color: '#94a3b8', flexShrink: 0 }} />}
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.label}</span>
                {opt.hint && <span style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>{opt.hint}</span>}
                {selected && <FaCheck size={9} style={{ color: '#0f172a', flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
};

TwDropdown.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    hint: PropTypes.string,
  })).isRequired,
  leftIcon: PropTypes.elementType,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  placeholder: PropTypes.string,
  minWidth: PropTypes.string,
  menuWidth: PropTypes.string,
  align: PropTypes.oneOf(['start', 'end']),
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default TwDropdown;
